import type { PaletteOptions } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import { qknColors } from 'app-components/utils/theme';
import { bizPaletteLight } from 'app-components/utils/theme/bizColors';

const lightPalette: PaletteOptions = {
  ...bizPaletteLight,

  // Financial Wellness: Figma "New Colors" (377:19059).
  wellnessScore: {
    groups: {
      control: { light: qknColors.sky[100], main: qknColors.sky[500], dark: qknColors.sky[700] },
      growth: { light: qknColors.blue[50], main: qknColors.blue[300], dark: qknColors.blue[500] },
      resilience: { light: qknColors.aqua[100], main: qknColors.aqua[400], dark: qknColors.aqua[700] },
    },
    chips: {
      excellent: { light: qknColors.green[100], dark: qknColors.green[900] },
      // Match the resolved Figma fill (#776827); its caption says yellow/1000.
      fair: { light: qknColors.yellow[50], dark: qknColors.yellow[900] },
      needsWork: { light: qknColors.cream[50], dark: qknColors.cream[900] },
    },
    scoreRange: {
      light: {
        1: qknColors.deepOrange[200],
        2: qknColors.cream[200],
        3: qknColors.yellow[200],
        4: qknColors.green[100],
        5: qknColors.green[200],
      },
      dark: {
        1: qknColors.deepOrange[400],
        2: qknColors.cream[500],
        3: qknColors.yellow[600],
        4: qknColors.green[400],
        5: qknColors.green[500],
      },
    },
  },

  /* A11y Settings */
  contrastThreshold: 4.5,
  tonalOffset: 0.3,

  /* Used for Buttons, (Primary Quicken Blue) */
  primary: {
    light: qknColors.blue[50],
    main: qknColors.blue[600],
    dark: qknColors.blue[900],
    contrastText: qknColors.common.white,
  },
  secondary: {
    light: qknColors.blue[100],
    main: qknColors.blue[600],
    dark: qknColors.blue[900],
    contrastText: qknColors.common.white,
  },

  /* Use these for Alerts, Chips & Statuses */
  success: {
    light: qknColors.green[50],
    main: qknColors.green[600],
    dark: qknColors.green[900],
    contrastText: qknColors.green[50],
  },
  pending: {
    light: qknColors.aqua[50],
    main: qknColors.aqua[600],
    dark: qknColors.aqua[900],
    fill: qknColors.aqua[300],
    contrastText: qknColors.common.white,
  },
  info: {
    light: qknColors.aqua[50],
    main: qknColors.aqua[600],
    dark: qknColors.aqua[900],
    contrastText: qknColors.aqua[50],
  },
  warning: {
    light: qknColors.cream[50],
    main: qknColors.cream[600],
    dark: qknColors.cream[900],
    contrastText: qknColors.common.white,
  },
  error: {
    // Delete, Error
    light: qknColors.red[50],
    main: qknColors.red[700],
    dark: qknColors.red[900],
    contrastText: qknColors.common.white,
  },

  inactive: {
    // closed, inactive, cancelled, disabled
    light: qknColors.grey[50],
    main: qknColors.grey[500],
    dark: qknColors.grey[700],
    contrastText: qknColors.common.white,
  },
  active: {
    // active, current, enabled, processing
    light: qknColors.blue[50],
    main: qknColors.blue[600],
    dark: qknColors.blue[900],
  },
  upcoming: {
    // upcoming
    light: qknColors.magenta[50],
    main: qknColors.magenta[700],
    dark: qknColors.magenta[900],
    contrastText: qknColors.common.white,
  },
  critical: {
    light: qknColors.deepOrange[100],
    main: qknColors.deepOrange[600],
    dark: qknColors.deepOrange[900],
    contrastText: qknColors.common.white,
  },

  /* Use for text, typography */
  text: {
    primary: qknColors.grey[950],
    secondary: qknColors.grey[700],
    tertiary: qknColors.grey[500],
    disabled: qknColors.grey[300],
    contrastText: qknColors.common.white,
  },

  /* Use for text, typography */
  icon: {
    light: qknColors.grey[500],
    main: qknColors.grey[700],
    dark: qknColors.grey[950],
    disabled: qknColors.grey[300],
  },

  /* Action colors: used in hover, disabled states, etc. for UX */
  action: {
    active: qknColors.grey[700], // The color of an active action like an icon button.
    hover: qknColors.blue[50], // The color of a hovered action.
    hoverOpacity: 0.04,
    // selected: qknColors.blue[100], // Remove
    selectedOpacity: 0.08,
    disabled: qknColors.grey[400], // The color of a disabled action.
    disabledBackground: qknColors.grey[100], // The background color of a disabled action.
    disabledOpacity: 0.45,
    focus: qknColors.blue[100],
    focusOpacity: 0.12,
    activatedOpacity: 0.12,
  },

  /* Backgrounds to be used in Box, Paper, Cards, Containers */
  background: {
    default: qknColors.grey[50], // Grey, Default BG, also used in dialog header
    paper: qknColors.grey[0], // White background used in Paper, Cards
    layer1: qknColors.grey[100], // BG for UI Components that needs contrast on default
    layer2: qknColors.grey[200], // Level 2 contrast
  },

  /* Backdrop for dialogs */
  backdrop: {
    light: qknColors.alpha[0],
    main: qknColors.alpha[500],
    dark: qknColors.alpha[950],
  },

  /* For glassy effect, use with backdrop */
  blur: {
    soft: 'blur(4px)',
    medium: 'blur(8px)',
    hard: 'blur(16px)',
  },

  /* Keyboard focus rings (composite box-shadows) */
  focusRings: {
    button: `
    0px 0px 0px 1px ${qknColors.grey[0]}, 
    0px 0px 0px 4px ${alpha(qknColors.blue[600], 0.24)}, 
    0px 0px 0px 1px ${qknColors.blue[600]} inset`,
  },

  /* Borders, dividers, Separators */
  border: {
    light: qknColors.grey[100],
    main: qknColors.grey[200],
    dark: qknColors.grey[300],
  },
  divider: qknColors.grey[100],

  /* Data, Amounts & Numbers */
  amount: {
    positive: qknColors.green[800],
    negative: qknColors.red[700],
    neutral: qknColors.grey[950],
  },

  /* Skeleton, Loading Ghosts */
  skeleton: {
    light: qknColors.grey[100],
    dark: qknColors.grey[200],
  },

  transferHistoryStatus: {
    pending: qknColors.blue[1000],
    pendingText: qknColors.blue[1000],

    processing: qknColors.blue[1000],
    processingText: qknColors.blue[1000],

    failed: qknColors.blue[1000],
    failedText: qknColors.blue[1000],

    completed: qknColors.blue[1000],
    completedText: qknColors.blue[1000],

    cancelled: qknColors.blue[1000],
    cancelledText: qknColors.blue[1000],

    closed: qknColors.blue[1000],
    closedText: qknColors.blue[1000],
  },

  qCards: {
    backgroundColor: qknColors.blue[800],
    titleColor: qknColors.common.white,
    contentColor: qknColors.common.white,
    closeColor: qknColors.common.white,
    highlightColor: qknColors.deepOrange[400],
  },
  register: {
    fontSize: {
      small: '12px',
      default: '14px',
      large: '16px',
    },
  },

  /* Charts & Graphs settings */
  chart: {
    chartHeight: 332,
    grid: qknColors.grey[50], // Axis grid lines
    today: qknColors.grey[800],
    averageLine: qknColors.deepOrange[500],
    targetLine: qknColors.green[500],
  },

  // Progress Bar
  progressBar: {
    container: qknColors.sky[100],
    fill: qknColors.sky[600],
  },

  data: {
    sequence: [
      qknColors.blue[600],
      qknColors.sky[500],
      qknColors.green[600],
      qknColors.cream[600],
      qknColors.yellow[600],
      qknColors.purple[600],
      qknColors.deepOrange[600],
      qknColors.magenta[600],
      qknColors.lime[600],
      qknColors.red[600],
      qknColors.aqua[600],
      qknColors.grey[500],
    ],
    sequenceLight: [
      qknColors.blue[100],
      qknColors.sky[100],
      qknColors.green[100],
      qknColors.cream[100],
      qknColors.yellow[100],
      qknColors.purple[100],
      qknColors.deepOrange[100],
      qknColors.magenta[100],
      qknColors.lime[100],
      qknColors.red[100],
      qknColors.aqua[100],
      qknColors.grey[100],
    ],
    sequenceDark: [
      qknColors.blue[900],
      qknColors.sky[900],
      qknColors.green[900],
      qknColors.cream[900],
      qknColors.yellow[900],
      qknColors.purple[900],
      qknColors.deepOrange[900],
      qknColors.magenta[900],
      qknColors.lime[900],
      qknColors.red[900],
      qknColors.aqua[900],
      qknColors.grey[900],
    ],
    stackedChartAsset: [
      qknColors.blue[600],
      qknColors.aqua[600],
      qknColors.green[600],
      qknColors.sky[500],
      qknColors.blue[400],
      qknColors.aqua[400],
      qknColors.green[400],
      qknColors.sky[300],
      qknColors.blue[200],
      qknColors.aqua[300],
      qknColors.green[200],
      qknColors.sky[100],
    ],
    stackedChartLiability: [
      qknColors.cream[600],
      qknColors.magenta[600],
      qknColors.yellow[600],
      qknColors.red[500],
      qknColors.cream[400],
      qknColors.magenta[400],
      qknColors.yellow[300],
      qknColors.red[400],
      qknColors.cream[200],
      qknColors.magenta[200],
      qknColors.lime[400],
      qknColors.red[200],
    ],
    stackedChartNet: qknColors.blue[800],
  },

  transactionFlags: {
    red: qknColors.red[600],
    orange: qknColors.deepOrange[600],
    yellow: qknColors.yellow[600],
    green: qknColors.green[600],
    // Figma's flag/blue and flag/violet token names are crossed relative to QCS/user-facing labels.
    blue: qknColors.sky[500],
    violet: qknColors.blue[500],
    brown: qknColors.cream[600],
    black: qknColors.grey[800],
    none: qknColors.transparent[0],
  },

  netWorth: {
    assetsSequence: [
      qknColors.green[600],
      qknColors.sky[500],
      qknColors.magenta[600],
      qknColors.purple[600],
      qknColors.blue[600],
      qknColors.aqua[600],
      qknColors.yellow[600],
      qknColors.cream[600],
      qknColors.deepOrange[600],
      qknColors.grey[500],
    ],
    debtSequence: [
      qknColors.yellow[600],
      qknColors.cream[600],
      qknColors.red[600],
      qknColors.deepOrange[600],
      qknColors.grey[500],
      qknColors.lime[600],
    ],
    ratioSequence: [
      qknColors.green[600],
      qknColors.green[400],
      qknColors.lime[500],
      qknColors.yellow[500],
      qknColors.cream[600],
      qknColors.red[400],
    ],
  },

  spendingPlan: {
    // Spending Plan Pie and legend Colors
    // https://www.figma.com/file/Uwd4YczoJNvOAecjfLMRYr/Design-Bugs?type=design&node-id=0%3A1&mode=design&t=RLSSokKXNblHHpJU-1
    piePlanned: qknColors.sky[600],
    pieOther: qknColors.purple[500],
    pieOverSpent: qknColors.deepOrange[500],
    // Planned Spending Colors
    plannedSpendingAvailable: qknColors.sky[100],
    plannedSpendingSpent: qknColors.sky[600],
    plannedSpendingOverSpent: qknColors.deepOrange[500],
    plannedSpendingRolloverPositive: qknColors.green[200],
    plannedSpendingRolloverNegativeLight: qknColors.deepOrange[100],
    plannedSpendingRolloverNegativeDark: qknColors.deepOrange[200],
    plannedSpendingBorderColor: qknColors.common.white,

    // Buckets
    available: qknColors.green[500],
    availableLight: qknColors.green[50],
    bills: qknColors.purple[500],
    billsLight: qknColors.purple[50],
    planned: qknColors.sky[500],
    plannedLight: qknColors.sky[50],
    other: qknColors.magenta[400],
    otherLight: qknColors.magenta[50],
    goals: qknColors.aqua[400],
    goalsLight: qknColors.aqua[50],
  },

  darkTooltip: {
    border: qknColors.grey[900],
    negative: qknColors.red[300],
    positive: qknColors.green[300],
  },

  savingsGoal: {
    barContainer: qknColors.sky[100],
    availableBar: qknColors.sky[600],
    spentBar: qknColors.purple[600],
  },

  /* Main usage based Color */
  legend: {
    income: qknColors.green[600], // legend color assigned to income recurrences
    incomeDark: qknColors.green[900],
    bill: qknColors.purple[600], // legend color assigned to bill recurrences
    billDark: qknColors.purple[900],
    expenses: qknColors.purple[600], // legend color assigned to bill recurrences
    refund: qknColors.sky[600], // legend color assigned to refund
    refundDark: qknColors.sky[900],
    net: qknColors.deepOrange[600],
  },
  legendDim: {
    income: qknColors.green[100], // legend color assigned to income recurrences
    bill: qknColors.purple[100], // legend color assigned to bill recurrences
    expenses: qknColors.purple[100], // legend color assigned to bill recurrences
    refund: qknColors.sky[100], // legend color assigned to refund
    net: qknColors.deepOrange[100],
  },
  // Surface tints for SeriesIcon (Getting started flow — Bill Setup, node 2129:47228);
  // Figma's legend light variables, distinct from the general-purpose legendDim tokens.
  seriesIconTint: {
    bill: '#DBD3FF',
    income: '#D8F3E3',
    refund: '#D3F7FE',
  },
  /* Credit Score based colors */
  creditScore: {
    poor: {
      main: qknColors.red[400],
      light: qknColors.red[50],
      contrastText: qknColors.red[800],
    },
    notBad: {
      main: qknColors.cream[600],
      light: qknColors.cream[50],
      contrastText: qknColors.cream[900],
    },
    good: {
      main: qknColors.green[400],
      light: qknColors.green[50],
      contrastText: qknColors.green[900],
    },
    excellent: {
      main: qknColors.green[600],
      light: qknColors.green[50],
      contrastText: qknColors.green[900],
    },
  },
  pendingProject: {
    light: qknColors.yellow[50],
    dark: qknColors.yellow[800],
  },

  // Spending trend bars (main chart)
  spending: {
    light: qknColors.blue[200],
    main: qknColors.blue[600],
    dark: qknColors.blue[800],
  },

  ai: {
    gradient: `linear-gradient(45deg, ${qknColors.blue[600]} 0%, ${qknColors.purple[400]} 100%)`,
    gradientRadial: `radial-gradient(83.14% 83.14% at 75% 9.09%, ${qknColors.purple[400]} 0%, ${qknColors.blue[600]} 100%)`,
    background: `linear-gradient(45deg, ${alpha(qknColors.blue[600], 0.1)} 0%, ${alpha(qknColors.green[400], 0.15)} 50%, ${alpha(qknColors.sky[500], 0.1)} 100%)`,
  },

  categoryIcon: {
    filter: 'none',
  },
};

export default lightPalette;
